import React, { useContext, useRef, useCallback, useEffect, useMemo } from "react";

//axios and base url
import { useLocation } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../src/BaseUrl";
import moment from "moment";
import { onlinePayment, handleKotItem,  customRound, parseParties} from "../resources/restaurant/auth/pos/newpos/helpers";
import { getFirebaseToken, getSystemSettings, getCookie, debouncePromise, offlineOrderSync, handleGetOrders, fetchOrderData } from './Functions';
import {db} from '../Firebase';
import { toast } from "react-toastify";
import { SettingsContext } from "../contexts/Settings";
import { UserContext } from "../contexts/User";
import { FoodContext } from "../contexts/Food";
import { RestaurantContext } from "../contexts/Restaurant";
import { ref, off, onValue, query, set} from "firebase/database";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { useGeneralHooks } from "../hooks/generalHooks";

  const toastObj = {
    position: "bottom-center",
    backgroundColor:"#0a8d6e",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  let authPromise = null;

  const authenticateFirebase = async (auth, getFirebaseToken, generalSettings) => {
    // Already authenticated
    if (auth.currentUser) {
      return auth.currentUser;
    }

    // If an authentication request is already in progress, wait for it
    if (!authPromise) {
      authPromise = (async () => {
        try {
          const cookie = await getFirebaseToken(
            getSystemSettings(generalSettings, "firebase_collection")
          );
        
          if (!cookie) {
            throw new Error("Firebase token not found");
          }
        
          const userCredential = await signInWithCustomToken(auth, cookie);
        
          if (!userCredential?.user?.uid) {
            throw new Error("Authentication failed");
          }
        
          return userCredential.user;
        } catch (error) {
          authPromise = null; // Reset so it can try again if it fails
          throw error;
        }
      })();
    }
  
    return authPromise;
  };

  // multiple re use functions for firabse etc.
  export const useCustomHooks = () => {
    const location = useLocation();
    const pathnameRef = useRef(location.pathname);
    useEffect(() => {
      pathnameRef.current = location.pathname;
    }, [location.pathname]);
    const { isOrderNo, taxType } = useGeneralHooks();
    const { generalSettings, setSyncOrder } = useContext(SettingsContext);
    const { authUserInfo, partyForSearch } = useContext(UserContext);
    const { orderTypeList, setPaginatedSubmittedOrders, setOrderCounter, setBellRing, audioInstance, workPeriodListForBranch } = useContext(RestaurantContext);
    const { getOnlineOrders,  getFoodItemsForPOS, getFoodItemsForCallCenter, getFoodPandaOrders } = useContext(FoodContext)
    const intervalRef = useRef(null);
    const onlineOrderQuery = useRef(null);
    const onlineOrder = useRef(false);

    const playNotificationSound = useCallback(() => {
      if (!audioInstance.current) {
        audioInstance.current = new Audio("/assets/beep/notify-new.mp3");
      } else {
        audioInstance.current.pause();
        audioInstance.current.currentTime = 0;
      }
      audioInstance.current.loop = true;
      audioInstance.current
        .play()
        .then(() => {
          setBellRing(true);
        })
        .catch((err) => {
          console.warn("Audio blocked until user interaction", err);
        });
    }, [audioInstance, setBellRing]);

    const multiplyAddon = useMemo(()=>{
      return parseInt(getSystemSettings(generalSettings, "online_order_addon")) === 1
    },[generalSettings]);
  
    // Wrap the fetchResults function with debounce
    const debouncedFetchResults = useCallback(debouncePromise((params, func, setLoading = null, id = null)=>func(params, setLoading, id), 2000), []);

    //firebase authentication debouncePromise
    const firebaseAuth = useCallback(async(url, callback)=>{
      const auth = getAuth();
      if(url){
        try {
          const user = await authenticateFirebase(auth, getFirebaseToken, generalSettings);
          if(user?.uid){
            const currentQuery = query(ref(db,url));
            // Detach any previous listeners
            const unsubscribe = onValue(
              currentQuery,
              (snapshot) => {
                const res = snapshot.val();
                console.log("Firebase onValue fired!", url, res);
                if(callback){
                  callback(null,res); // Resolve the promise with the result
                }
              },
              (error) => {
                if(callback){
                  callback(error,null); // Resolve the promise with the result
                }
              }
            );
            return () => {
              unsubscribe();
            };
          }else{
            if(callback){
              callback(new Error("Authentication failed"), null);
            }
          }
        } catch (error) {
          if (typeof callback === "function") callback(error, null);
        };
      }else{
        if (typeof callback === "function") callback(new Error("Url not found"), null);
      }
    },[generalSettings])

    const firebaseAuthSetData = useCallback(async(url, data)=>{
      const auth = getAuth();
      if(url){
        try {
          const user = await authenticateFirebase(auth, getFirebaseToken, generalSettings);
          if(user?.uid){
            try{
              const res = await set(ref(db, url), data);
              return "Data written successfully!"
            }catch(err){
              return err
            }
          }else{
            return new Error("Authentication failed")
          }
        } catch (error) {
          return error
        };
      }else{
        return new Error("Url not found")
      }
    },[generalSettings])

    //tax discount
    const checkTaxType = useCallback((subtotal, dis) =>{
      const taxTypeDiscount = getSystemSettings(generalSettings, "tax_type_discount") === "tax_discount_after";
      const taxTypeFixed = parseInt(getSystemSettings(generalSettings, "fixed_inclusive_tax")) !== 1;
      if(taxTypeDiscount && taxTypeFixed){
        return Number(subtotal) - Number(dis)
      }else{
        return Number(subtotal)
      }
    },[generalSettings]);

    //allow qty and amount in points
    const qtyAmountPoints = useCallback(() =>{
      const roundOff = parseInt(getSystemSettings(generalSettings, "stock_round_off"));
      if (roundOff === 0) return "1";
      return Number("0." + "0".repeat(roundOff - 1) + "1");
    },[generalSettings]);

    //round off qty amount stock
    const roundOffStock = useCallback((price) =>{
      const roundOff = parseInt(getSystemSettings(generalSettings, "stock_round_off"));
      if(roundOff > 0){
        return Number(Number(price).toFixed(roundOff))
      }else{
        return Math.round(price)
      }
    },[generalSettings]);

    //round off price
    const roundOffPrice = useCallback((price) =>{
      const roundOff = parseInt(getSystemSettings(generalSettings, "order_round_off"));
      if(roundOff > 0){
        return Number(Number(price).toFixed(roundOff))
      }else{
        return Math.round(price)
      }
    },[generalSettings]);

    //round off price
    const roundFormatPrice = useCallback((price) =>{
      const roundOff = parseInt(getSystemSettings(generalSettings, "order_round_off"));
      let localCurrency = JSON.parse(localStorage.getItem("currency"));
      if (localCurrency !== null) {
        if(roundOff > 0){
          return (Number(localCurrency.rate * price).toFixed(roundOff))
        }else{
          return Math.round(localCurrency.rate * price)
        }
      }else{
        return 0
      }
    },[generalSettings]);

    //set after tax price on inclusive settle 
    const setCardItemInclusiveSettle = (prevCartItems,pre_tax,b_tax,loyalty=false) => {
      let subtotal = 0;
      let newData = prevCartItems;
      if(typeof(prevCartItems) === "object" && !Array.isArray(prevCartItems)){
        newData = {...prevCartItems};
        prevCartItems && Object.keys(prevCartItems).map(index=>{
          const price = roundOffPrice(newData[index].after_tax_price * (1 + (pre_tax / 100)));
          const newPrice = Number(price / (1 + (b_tax / 100)));
          subtotal += (newPrice);
          if(loyalty){
            const isLoyaltyItem = index.endsWith("loyalty");
            if(isLoyaltyItem) {
              // Removing loyalty item (reset status)
              newData[index.replace("loyalty", "")] = {
                ...newData[index],
                after_tax_price: roundOffPrice(newPrice),
                status: "",
              }
              delete newData[index]
            } else {
              // All other items remain same, but reset unwanted loyalty status
              newData[index] = {
                ...newData[index],
                after_tax_price: roundOffPrice(newPrice),
                status: newData[index].status === "loyalty" ? "" : newData[index].status,
              }
            }
          }else{
            newData[index] = {
              ...newData[index],
              after_tax_price: roundOffPrice(newPrice),
            }
          }
        });
      }else{
        newData = prevCartItems && [...prevCartItems].map((item)=>{
          const price = roundOffPrice(item.price);
          const newPrice = Number(price / (1 + (b_tax / 100)));
          subtotal += (newPrice);
          return {
            ...item,
            after_tax_price: roundOffPrice(newPrice),
          }
        });
      }
      return {updatedCartItems: newData, subtotal};
    };

    //get subtotal on inclusive settle
    const getSubtotalInclusiveSettle = useCallback((orderItems,subtotal,settleCond) => {
      if(parseInt(getSystemSettings(generalSettings, "fixed_inclusive_tax")) === 1 &&
       (getSystemSettings(generalSettings, "tax_type") === "inclusive") && settleCond){
        const subTotal = orderItems.reduce((sum,item)=> sum + item.after_tax_price,0);
        return roundOffPrice(subTotal)
      }else{
        return roundOffPrice(subtotal);
      }
    },[generalSettings]);

    //show price of each item in print
    const showPriceOfEachOrderItemPrint = useCallback((thisItem, branchTax, posAddon = true) => {
      let price = 0;
      let tempPropertyPrice = 0;
      if (thisItem.properties !== null && thisItem.properties !== "") {
        let propertyItems = JSON.parse(thisItem.properties);
        propertyItems.forEach((propertyItem) => {
          let temp;
          const base = propertyItem.quantity * propertyItem.price_per_qty;
          if(!posAddon && !multiplyAddon){
            temp = base;
          }else{
            temp = base * thisItem.quantity
          }
          tempPropertyPrice = tempPropertyPrice + temp;
        });
      }
      price = Number(thisItem.price) - tempPropertyPrice;
      if((thisItem.status === "foc" || thisItem.status === "complimentory") && thisItem.after_tax_price == 0){
        return 0
      }else if (taxType === "inclusive") {
        let finalPrice = price / (1 + (branchTax / 100));
        return roundFormatPrice(finalPrice);
      }else{
        return roundFormatPrice(price);
      }
    },[taxType]);

    // show property price
    const showPropertyPrice= useCallback((qty,price,branchTax)=>{
      let propPrice = 0;
      if (taxType === "inclusive") {
        propPrice = qty * price;
        let finalPrice = propPrice / (1 + (branchTax / 100));
        return roundFormatPrice(finalPrice);
      }else{
        propPrice = qty * price;
        return roundFormatPrice(propPrice);
      }
    },[taxType])

    const getBeforeTaxPricePayment = useCallback((price, theTax) => {
      return (Number(price) - (Number(price) / (1 + (theTax / 100)))).toFixed(2);
    },[generalSettings]);

    //offline
    const handleSubmitOffline = useCallback(async(formData,url) =>{
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
    
        const data = await response.json();
        if (!response.ok) {
          // throw an error with status + message
          if(data?.status === 0){
            toast.error(data?.message, toastObj);
            return false;
          }
        }
    
        // Show success toast if data is returned
        if (data) {
          toast.success(("Order Saved To Local"), toastObj);
          return true;
        }
      } catch (error) {
        if(error?.status === 0){
          toast.error(error?.message, toastObj);
          return false;
        }
        if(navigator.onLine){
          toast.error(("Please contact support"), toastObj);
        }else{
          toast.error(("Please check internet"), toastObj);
        }
        return false;
      }
    },[]);
  
    // total cash card price acc to tax
    const totalPriceWithTax = useCallback((total,subtotal,vat,discount,branch,key) =>{
      let totalPrice = parseFloat(total);
      const foundKey = branch && branch.payment_tax && Object.keys(branch.payment_tax).find(k => k.toLowerCase() === key);
      if(foundKey && branch.payment_tax[foundKey] && branch.payment_tax[foundKey] > 0){
        const taxTypeDiscount = getSystemSettings(generalSettings, "tax_type_discount") === "tax_discount_after";
        const taxTypeFixed = parseInt(getSystemSettings(generalSettings, "fixed_inclusive_tax")) !== 1;
        const calAmount = (taxTypeDiscount && taxTypeFixed) ? (parseFloat(subtotal) - parseFloat(discount)) : parseFloat(subtotal);
        const inclusive_vat = calAmount * (parseFloat(branch.payment_tax[foundKey]) / 100);
        const previous_amount = parseFloat(total) - parseFloat(vat);
        totalPrice = previous_amount + inclusive_vat;
      }
      const finalPrice = roundFormatPrice(totalPrice);
      return [finalPrice,foundKey && branch && branch.payment_tax[foundKey]]
    },[generalSettings]);
    
    //cal net bill cash card
    const renderNetBill = useCallback((total, cartSubTotal, theVat, discount, branch, key, label) => {
      const [netBill, percent] = totalPriceWithTax(
        total,
        cartSubTotal,
        theVat,
        discount,
        branch,
        key
      );
      return (
        <div className="d-flex align-items-center justify-content-between my-1 mx-1">
          <div className="fk-print-text genAcc-select text-capitalize">
            {label}{percent > 0 && ` (${percent}%)`}
          </div>
          <div className="fk-print-text genAcc-select text-capitalize">
            {netBill}
          </div>
        </div>
      );
    },[generalSettings]);
  
    //tax percent
    const taxPercent = useCallback((total,vat,discount) => {
      if(parseFloat(vat) > 0){
        const taxTypeDiscount = getSystemSettings(generalSettings, "tax_type_discount") === "tax_discount_after";
        const taxTypeFixed = parseInt(getSystemSettings(generalSettings, "fixed_inclusive_tax")) !== 1;
        if(taxTypeDiscount && taxTypeFixed){
         let totalAmount = parseFloat(total) - parseFloat(discount);
         let vatPercentage = ((parseFloat(vat) / totalAmount) * 100);
         return customRound(vatPercentage);
        }else{
          return customRound((parseFloat(vat)/parseFloat(total)) * 100) 
        }
      }else{
        return 0;
      }
    },[generalSettings])
   
    //get order counter
    const getOnlineOrderCount = useCallback(async(order) =>{
      const [online, ready, foodPanda] = order;

      let type = "";
      if (pathnameRef.current === "/dashboard/pos/online-orders") {
        type = "online";
      } else if (pathnameRef.current === "/dashboard/pos/foodpanda") {
        type = "fp";
      }
      console.log("Abcd99",type,order)
      const processNotification = () => {
        const runNotifications = () => {
          if (online > 0) {
            toast.success(`You have recieved (${online}) online order`, { ...toastObj, toastId: "online-notify" });
          }
          if (foodPanda > 0) {
            setTimeout(() => {
              toast.success(`You have recieved (${foodPanda}) food panda order`, { ...toastObj, toastId: "fp-notify" });
            }, 1000);
          }
          if (ready > 0) {
            setTimeout(() => {
              toast.success(`Running order(s) updated. Please check.`, { ...toastObj, toastId: "ready-notify" });
            }, 2000);
          }
        };
        
        runNotifications();

        if(online > 0 || ready > 0 || foodPanda > 0) {
          playNotificationSound();
        }
        setOrderCounter((curr) => {
          const newData = {
            ...curr,
            online: online,
            foodPanda: foodPanda,
            ready: ready,
          }
          return newData;
        });
      };

      if(type === "online"){
        if(online > 0){
          setTimeout(() => {
            processNotification();
            getOnlineOrders();
          }, 4000);
        } else {
          processNotification();
        }
      }else if(type === "fp"){
        if(foodPanda > 0){
          setTimeout(() => {
            processNotification();
            getFoodPandaOrders();
          }, 4000);
        } else {
          processNotification();
        }
      }else{
        processNotification();
      }
    },[getOnlineOrders, getFoodPandaOrders])
  
    //online order data
    const onlineOrders = useCallback(async() => {
      const pendingUrl = BASE_URL + "/website/pending-order";
      try{
        const res = await axios.get(pendingUrl, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        if(res){
          console.log("Abcd99",res)
          if (res.data) {
            if(res.data?.length){
              getOnlineOrderCount(res.data);
            }
          }
        }
      }catch (error){
        console.log("error order pending api",error)
      }     
    },[])
  
    //firbase online order
    const getFirbaseOnlineOrder = useCallback((type = "") =>{
      if(authUserInfo?.details){
        let url = "pos/" + getSystemSettings(generalSettings, "firebase_collection") + "/online_orders";
        if(authUserInfo.details?.user_type === "staff" || authUserInfo.details?.user_type === "manager"){
          url = "pos/" + getSystemSettings(generalSettings, "firebase_collection") + "/online_orders/" + authUserInfo?.details?.branch_id
        }
       
        return firebaseAuth(url, (error, res) => {
          if (error) {
            console.error("Firebase Error:", error);
            return;
          }

          if(res){
            let anyNotified = false;
            if(authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager"){
              if(res?.message === "Notified"){
                console.log("Abcd45",res.message)
                anyNotified = true;
              }
            }else{
              // Loop through the objects
              for (const key in res) {
                if (res[key]?.message === "Notified") {
                  anyNotified = true;
                  break; // Stop the loop once a "Notified" message is found
                }
              }
            }
            if(anyNotified){
              console.log("Abcd46",anyNotified)
              onlineOrders()
            }
          }
        })
      }
      
    },[generalSettings, authUserInfo]);
  
    //firebase online oder
    const getFirbaseRunningOrder = useCallback(() =>{
      if(authUserInfo?.details){
        let url = "pos/" + getSystemSettings(generalSettings, "firebase_collection") + "/newOrder";
        if(authUserInfo.details?.user_type === "staff" || authUserInfo.details?.user_type === "manager"){
          url = "pos/" + getSystemSettings(generalSettings, "firebase_collection") + "/newOrder/" + authUserInfo?.details?.branch_id
        }
        
        const startTime = Date.now();
        const unsubscribePromise = firebaseAuth(url, (error, res) => {
          if (error) {
            console.error("Firebase Error:", error);
            return;
          }
          if (Date.now() - startTime < 5000) {
            return; // Ignore the initial payload on first load
          }
          console.log("Abcd88",res)
          runningOrderNotification(res, url)
        });
        return () => {
          if (unsubscribePromise) {
            unsubscribePromise.then(unsub => {
              if (typeof unsub === "function") unsub();
            });
          }
        };
      }
      return () => {};
      
    },[generalSettings, authUserInfo]);

    const runningOrderNotification = useCallback((res, url) =>{
      if(res){
        let anyNotified = false;
        let bellRingNotify = null;
        let targetUrl = url;

        if(authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager"){
          if(res?.message === "Notified"){
            anyNotified = true;
            if(res?.is_bell_ring){
              bellRingNotify = {...res, message: "Listened", is_bell_ring: 0}
            }
          }
        }else{
          // Loop through the objects
          for (const key in res) {
            if (res[key]?.message === "Notified") {
              anyNotified = true;
              if(res[key]?.is_bell_ring){
                bellRingNotify = {...res[key], message: "Listened", is_bell_ring: 0}
                targetUrl = url + "/" + key;
              }
              break; // Stop the loop once a "Notified" message is found
            }
          }
        }

        if (bellRingNotify && targetUrl) {
          playNotificationSound();
          setTimeout(() => {
            firebaseAuthSetData(targetUrl, bellRingNotify);
          }, 2500);
        }
        //
        if(pathnameRef.current === "/dashboard/pos/submitted"){
          console.log("Abcd88",res)
          if(anyNotified){
            setPaginatedSubmittedOrders(1)
          }
        }
      }
    },[getFirbaseRunningOrder])

    //firbase online order
    const getAutoAccOnlineOdr = useCallback(() => {
      if (authUserInfo?.details && authUserInfo.details?.user_type === "staff" || authUserInfo.details?.user_type === "manager") {
        let url = "pos/" + getSystemSettings(generalSettings, "firebase_collection") + "/onlineautoaccept/" + authUserInfo?.details?.branch_id
        console.log("data", url)
        const unsubscribePromise = firebaseAuth(url, async (error, res) => {
          if (error) {
            console.error("Firebase Error:", error);
            return;
          }
          if (res) {
            console.log("data46", res)
            if(res?.status){
              const fbOrder = {
                ...res,
                status: false,
              }
              if(res?.orderId && authUserInfo.details?.id === parseInt(res?.userId)){
                const payload = {
                  id: res.orderId
                }
                const data = await fetchOrderData(payload);
                firebaseAuthSetData(url, fbOrder)
                if(!data || data?.status){
                  toast.error("order id not found");
                  return;
                }
                console.log("data56", data)
                handleLocalPrint(data)
              }
            }
          }
        });
        return () => {
          if (unsubscribePromise) {
            unsubscribePromise.then(unsub => {
              if (typeof unsub === "function") unsub();
            });
          }
        };
      }
      return () => {};

    },[authUserInfo, generalSettings])
  
    const checkOnlineOrders = useCallback((type = "") =>{
      if(authUserInfo?.details){
        if(getSystemSettings(generalSettings, "firebase_collection")){
          const unsubscribePromise = getFirbaseOnlineOrder(type);
          return () => {
            if (unsubscribePromise) {
              unsubscribePromise.then(unsub => {
                if (typeof unsub === "function") unsub();
              });
            }
          };
        }else{
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          intervalRef.current = setInterval(() => {
            onlineOrders();
          }, 90000);
          return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
          };
        }  
      }
      return () => {};
    },[generalSettings, authUserInfo])
  
    // stock qty from firebase
    const fetchStock = useCallback((branch = null) => {
      if(authUserInfo?.details){
        let stockUrl;
        if(authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager"){
          stockUrl = 'pos/' + getSystemSettings(generalSettings, "firebase_collection") + '/finished_goods/' + authUserInfo.details?.branch_id;
        }else if(branch){
          stockUrl = 'pos/' + getSystemSettings(generalSettings, "firebase_collection") + '/finished_goods/' + branch?.id;
        }
        
        if (stockUrl) {
          const unsubscribePromise = firebaseAuth(stockUrl, (error, data) => {
            if (error) {
              console.error("Error in fetchDataAndProcess:", error);
              return;
            }
            if(data){
              if(authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager"){
                getFoodItemsForPOS();
              }else{
                getFoodItemsForCallCenter(branch?.id)
              }
            }
          });
          
          return () => {
            if (unsubscribePromise) {
              unsubscribePromise.then(unsub => {
                if (typeof unsub === "function") unsub();
              });
            }
          };
        }
      }
      return () => {};
    },[generalSettings, authUserInfo, firebaseAuth, getFoodItemsForPOS, getFoodItemsForCallCenter]);

    //parties
    const availableDeliveryParties = useMemo(()=>{
      if (
        partyForSearch && partyForSearch.length &&
        authUserInfo?.details
      ) {
        let filterParties = partyForSearch;
        if (authUserInfo.details?.user_type === "call_center") {
          filterParties = partyForSearch.filter((party) => party.for_cc === 1)
        } else if (authUserInfo.details?.user_type === "staff" || authUserInfo.details?.user_type === "waiter" || authUserInfo.details.user_type === "manager") {
          filterParties = partyForSearch.filter((party) => party.for_cc === 0)
        }
        const parties = parseParties(filterParties);
        return parties
      }
    },[partyForSearch, authUserInfo]);

    //orderType pos
    const availableDeliveryTypes = useMemo(()=>{
      if (
        orderTypeList && orderTypeList.data &&
        orderTypeList.data.length && authUserInfo?.details
      ) {
        const filteredDeliveryTypes = orderTypeList.data.filter(
          (order) => {
            const isisOrderTypeActive = order.status === "On";
            const userOdT = (authUserInfo.details?.order_type_id && typeof (authUserInfo.details.order_type_id) === "string")
              ? JSON.parse(authUserInfo.details.order_type_id) : [];
            const orderName = order.name?.toLowerCase();
            const isOnline = orderName === "delivery" || orderName === "takeaway";  

            if (authUserInfo.details.order_type_id && userOdT?.length > 0) {
              const orderType = isisOrderTypeActive && userOdT.includes(order.id);
              if (orderType && isOnline) {
                onlineOrder.current = true
              }
              return orderType
            }
            if (isisOrderTypeActive && isOnline) {
              onlineOrder.current = true
            }
            return isisOrderTypeActive
          }
        );
        return filteredDeliveryTypes
      }
    },[orderTypeList, authUserInfo])
    
    // set customer screen data
    const handleCustomerOrderUpdate = useCallback(async (data=null) => {
      try {
        let customerScreenUrl;
        if(authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager"){
          customerScreenUrl = 'pos/' + getSystemSettings(generalSettings, "firebase_collection") + "/" + authUserInfo.details?.branch_id + '/customer_order/' + authUserInfo.details?.id;
        }
        const res = await firebaseAuthSetData(customerScreenUrl, data);
        if(res){
          return res;
        }
      } catch (error) {
        console.error('Error updating document:', error);
      }
    },[generalSettings, authUserInfo]);

    const getDynamicOfflinePrintUrl = useCallback((endpoint) => {
      if(!authUserInfo || !generalSettings) return "";
      if(authUserInfo.details?.offline_url){
        return `${authUserInfo.details.offline_url}` + endpoint;
      }
      if(generalSettings?.offline_ip){
        return `${generalSettings?.offline_ip}` + endpoint;
      }

      return `http://localhost:8081` + endpoint;

    },[generalSettings, authUserInfo]);

    const getDynamicOfflineApiUrl = useCallback((endpoint) => {
      if(!authUserInfo || !generalSettings) return "";
      if(authUserInfo.details?.offline_url){
        return `${authUserInfo.details.offline_url}/offline_pos/api` + endpoint;
      }
      if(generalSettings?.offline_ip){
        return `${generalSettings?.offline_ip}/offline_pos/api` + endpoint;
      }

      return `http://localhost:8081/offline_pos/api` + endpoint;

    },[authUserInfo, generalSettings]);

    const getOfflineApiUrl = useCallback((endpoint) => {
      if(authUserInfo?.details){
        // Check if offline mode is enabled
        const isOfflineEnabled = parseInt(authUserInfo.details?.offline_enable) === 1;
        if (isOfflineEnabled) {
          // return `http://localhost:8081/offline_pos/api` + endpoint;
          return getDynamicOfflineApiUrl(endpoint);
        } else {
          return BASE_URL + endpoint;
        }
      }
    },[authUserInfo]);

    const offlineOrderSyncToLocal = useCallback(async (orderId) => {
      const endPoint = "/get-offline-sync-orders-update";
      const url = getDynamicOfflineApiUrl(endPoint);
      const formData = { order_id: orderId };
      try {
        const result = await axios.post(url, formData, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        if (result?.data) {
          return result.data;
        }
      } catch (err) {
        return false;
      }
    }, [getDynamicOfflineApiUrl]);
    
  
    //sync local order
    const syncLocalOrders = useCallback(async (type = "") => {
      setSyncOrder(true);
      try{
        const endPoint = "/get-offline-sync-orders";
        const localUrl = getDynamicOfflineApiUrl(endPoint);
        const getOrders = await handleGetOrders(localUrl);
        if(!getOrders || getOrders.length === 0){
          if(type === "sync"){
            toast.error("No offline orders to sync", toastObj);
          }
          setSyncOrder(false);
          return;
        }
        if(type === "sync"){
          toast.error(`${getOrders.length} order need to be synced`);
        }
        let count = 0;
        if(getOrders && getOrders?.length){
          let onlineSync = "";
          for (const order of getOrders) {
            await new Promise(resolve => setTimeout(resolve, 3000));
            try {
              onlineSync = await offlineOrderSync(order);
              console.log("abcd43",order, onlineSync)
              if(onlineSync === "ended"){
                toast.error("The user's previous shift has ended", toastObj);
                break;
              }else if(onlineSync){
                count++;
                await offlineOrderSyncToLocal(onlineSync)
              }else{
                toast.error("Error occured during sync", toastObj)
                break;
              }
            } catch (error) {
              console.error("Error:", error);
            }
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
          if(count >= getOrders?.length){
            toast.success("Settle order has been restored",toastObj)
          }
          if(onlineSync && onlineSync !== "ended"){
            const endPointCount = "/get-sync-order-count";
            const localUrlCount = getDynamicOfflineApiUrl(endPointCount);
            const orderCount = await handleGetOrders(localUrlCount);
            if(Number(orderCount?.total_records) > 0){
              syncLocalOrders();
            }
          }
          setSyncOrder(false);
        }
      }catch{
        setSyncOrder(false);
        toast.error("Can't get orders", toastObj)
      }
    },[getDynamicOfflineApiUrl]);

    const handleLocalPrint = useCallback((data) => {
      let formData = formDataKotReturn(data);
      let axiosConfig = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      axios
        .post(getDynamicOfflinePrintUrl("/example-app/api/test"), formData, axiosConfig)
        .then((res) => {
          console.log("response",res)
        }).catch((err) => {
          console.log("error",err)
        });
    },[generalSettings, authUserInfo, getDynamicOfflinePrintUrl]);

    // form data for direct print to printer
    const formDataKotReturn = (item) => {
      const data = item;
      const workPeriod = workPeriodListForBranch &&
      workPeriodListForBranch.find((tempWorkPeriod) => {
        return (data.branch_id === parseInt(tempWorkPeriod.branch_id)) && tempWorkPeriod.ended_at === null;
      });
      //cash card tax wise total 
      const [cardAmount, cardPercent] = totalPriceWithTax(data.total_payable,data.order_bill, data.vat,data.discount_amount, data,"card");
      const [cashAmount, cashPercent] = totalPriceWithTax(data.total_payable,data.order_bill, data.vat,data.discount_amount, data,"cash");

      const isPOS = item?.request_from === "POS";
      const shouldMultiply = multiplyAddon || isPOS;

      let formatTime = moment(data.created_at).format('DD-MMM-YYYY hh:mm A');
      const ref_no = data.party_name +  " - ref no :"  + data?.ref_no
        return {
          site_name: getSystemSettings(generalSettings, "siteName"),
          branch: data.theBranch,
          type_print_heading: getSystemSettings(
            generalSettings,
            "type_print_heading"
          ),
          payment_type: onlinePayment(data),
          orderItems: handleKotItem(item),
          serviceCharge: data.delivery_charge,
          service_amount: 0,
          view_cash_card: parseInt(getSystemSettings(generalSettings,"view_cash_card")),
          cash: cashAmount,
          card: cardAmount,
          cash_percent: cashPercent,
          card_percent: cardPercent,
          multiplyAddon: shouldMultiply,
          discount: data.discount_percentage,
          order_type: data.trans_type,
          subTotal: data.order_bill,
          totalPayable: data.total_payable,
          theVat: data.vat,
          tax_percent: data.tax_percent,
          print_tax : data.theBranch?.print_tax,
          RefNo: ref_no,
          print_footer: getSystemSettings(generalSettings, "type_print_footer"),
          print_bill_footer: getSystemSettings(generalSettings, "print_bill_footer"),
          kot_header: getSystemSettings(generalSettings, "kot_header"),
          user_name: authUserInfo && authUserInfo.details && authUserInfo.details.name,
          tax_type: taxType,
          created_by : data?.user_name,
          rider_name: data?.delivery_boy_name ? data.delivery_boy_name : "",
          rider_notes: data?.note_to_rider ? data.note_to_rider : "",
          order_tip: data?.order_tip,
          //customer print
          customerIp: data.theBranch.kitchen_printer_ip != null && JSON.parse(data.theBranch.kitchen_printer_ip)?.[authUserInfo.details.id] ? JSON.parse(data.theBranch.kitchen_printer_ip)[authUserInfo.details.id] : null,
          deliveryIp: data.theBranch.customer_counter_ip != null && data.theBranch.customer_counter_ip !== "" ? data.theBranch.customer_counter_ip : null,
          customerOrder: data.orderedItems,
          party_name: data.trans_type,
          dept_tag: {
            id: data.dept_tag_id,
            name: data.dept_tag_name,
          },
          orderTypeDetails: {
            orderTypeId: data.order_type_id,
            orderTypeName: data.order_type_name,
            code: data?.order_type_code ?? "",
          },
          partyDetails: {
            partyId: data.party_id,
            partyName: data.party_name,
          },
          is_online: 1,
          guest: 0,
          is_reservation: false,
          isSettle: 0,
          table: null,
          waiter: null,
          payment_amount: "",
          card_number: "",
          newCustomer: 0,
          newCustomerInfo: {
            name: data.customer_name,
            email:"",
            address: data.delivery_address,
            area_name: data?.area_name ?? "",
            ref_no: data.ref_no,
            phn_no: data.delivery_phn_no,
            nearest_landmark: data.nearest_landmark,
            alt_phn_no: data?.alt_phn_no,
          },
          token: {
            id: (isOrderNo) ? (data.order_no ?? data.document_no) : data.token?.id,
            time: formatTime,
            token: data.token,
            order_id: (data?.order_id ?? data.id),
          },
          total_guest: 1,
          table_id: null,
          table_name: null,
          discount_voucher: "",
          scheme: {
            id: null,
            discount: data?.discount ?? 0,
            how_much_discount: data.discount_amount ?? 0,
          },
          paidMoney: 0,
          dept_commission: "",
          workPeriod: workPeriod ? workPeriod : {
            id: data.work_period_id,
            token: data.token
          },
          order_id: null,
          orderFoodGroups: null,
          selectedPropertyGroup: [],
          return_amount: 0,
          fbr_num: "",
          srb_num: "",
       
        };
    };
  
    return {multiplyAddon, getFirbaseOnlineOrder, syncLocalOrders, getAutoAccOnlineOdr, handleSubmitOffline, getDynamicOfflinePrintUrl, getOfflineApiUrl,
      checkTaxType, getSubtotalInclusiveSettle, setCardItemInclusiveSettle, roundFormatPrice, handleLocalPrint, 
      showPriceOfEachOrderItemPrint, showPropertyPrice, getBeforeTaxPricePayment, handleCustomerOrderUpdate,
      debouncedFetchResults, getFirbaseRunningOrder, fetchStock, audioInstance, firebaseAuth, playNotificationSound,
      onlineOrderQuery, intervalRef, qtyAmountPoints, roundOffStock, roundOffPrice, taxPercent,
      totalPriceWithTax, onlineOrders, renderNetBill, checkOnlineOrders,getDynamicOfflineApiUrl, availableDeliveryParties,
      availableDeliveryTypes, onlineOrder, firebaseAuthSetData
    }
  }